import { faCheck, faChevronDown, faSearch, faTimes } from "@outschool/icons";
/**
 * Figma file: https://www.figma.com/file/vG9GNXiYwiuPpss0kVT8se/%E2%AD%90-Backpack-Components-(2023)?type=design&node-id=2545-32701&mode=design&t=020qhwN2bNZFv5CL-0
 *
 */
import React from "react";

import { THEME } from "../../theme";
import Box from "../Box/Box";
import { Button } from "../Button/Button";
import { IconButton, IconButtonProps } from "../IconButton/IconButton";
import { Menu, MenuItem } from "../Menu/Menu";
import Icon from "../Icon/Icon";
import {
  Input,
  InputProps,
  useMediaQuery,
  useTheme,
  Theme,
} from "@mui/material";

type OSInputProps = {
  getInputProps?: () => object;
};

enum SearchBoxVariant {
  inApp = "InApp",
  navbar = "Navbar",
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface SearchBoxFilterOptions {
  options: FilterOption[];
  selectedOption: FilterOption | null;
  label: string;
  onChange: (value: FilterOption | null) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface SearchBoxProps {
  /**
   * Function to handle the form sending
   */
  onSubmit: (e: any) => void;
  /**
   * Function to clear out the input value
   */
  onClear: () => void;
  /**
   * Label to be set on the inApp variant button
   */
  searchLabel?: string;
  /**
   * Props forwarded to the input field
   */
  inputProps?: OSInputProps & InputProps;
  /**
   * Ref to the clear button that shows up when the value is present
   */
  clearSearchInputBoxRef?: React.RefObject<HTMLButtonElement>;
  /**
   * String for the aria label of the clear search icon
   */
  clearSearchAriaLabel?: string;
  /**
   * Component to render on the left side of the search input
   * @deprecated Do not use, if you need any customization here, please ask in #design-system channel
   */
  leftComponent?: React.ReactNode;
  /**
   * Filter configuration object
   *
   * @param options - Array of filter options with label and value
   * @param selectedOption - Currently selected option (or null)
   * @param label - Display label for the filter button
   * @param onChange - Callback when selection changes
   * @param onOpen - Optional callback when filter menu opens
   * @param onClose - Optional callback when filter menu closes
   *
   * @example
   * ```typescript
   * const learnerFilter = {
   *   options: [
   *     { label: "All Classes", value: "all" },
   *     { label: "John Doe", value: "john" }
   *   ],
   *   selectedOption: { label: "All Classes", value: "all" },
   *   label: "Filter by learner",
   *   onChange: handleFilterChange
   * };
   *
   * <SearchBox filter={learnerFilter} />
   * ```
   */
  filter?: SearchBoxFilterOptions;
}

export function NavbarSearchBox(props: SearchBoxProps) {
  return <SearchBox {...props} variant={SearchBoxVariant.navbar} />;
}

export function InAppSearchBox(props: SearchBoxProps) {
  return <SearchBox {...props} variant={SearchBoxVariant.inApp} />;
}

export type SearchBoxComponentType =
  | typeof NavbarSearchBox
  | typeof InAppSearchBox;

function SearchBox({
  inputProps: {
    ref: inputRef,
    placeholder,
    value,
    onBlur,
    onFocus,
    onChange,
    spellCheck,
    getInputProps = () => ({}),
  } = {},
  onSubmit,
  onClear,
  clearSearchInputBoxRef,
  clearSearchAriaLabel = "Clear Search",
  searchLabel = "Search",
  variant,
  leftComponent,
  filter,
}: SearchBoxProps & {
  variant: SearchBoxVariant;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      component="form"
      sx={(theme: Theme) => ({
        backgroundColor: "white",
        border: "2px solid",
        borderColor: "neutral.300",
        borderRadius: "999px",
        flex: 1,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px",
        transition: "box-shadow 0.3s ease-in-out",
        "&:hover": {
          boxShadow:
            "0px 8px 16px 0px rgba(0, 0, 0, 0.08), 0px 0px 4px 0px rgba(0, 0, 0, 0.04);",
        },
        "&:focus-within": {
          outline: "2px solid primary.main",
          outlineOffset: "2px",
        },
        [theme.breakpoints.down("md")]: {
          padding: "3px 4px",
        },
      })}
      onSubmit={onSubmit}
    >
      {leftComponent}
      {filter && (
        <FilterButton
          options={filter.options ?? []}
          selectedOption={filter.selectedOption ?? null}
          label={filter.label}
          onChange={filter.onChange}
          onOpen={filter.onOpen}
          onClose={filter.onClose}
        />
      )}
      <Box sx={{ width: "1.6rem" }} />
      {variant === SearchBoxVariant.inApp && (
        <Responsive
          Desktop={
            <SearchIcon
              aria-label={searchLabel}
              sx={{ color: "neutral.900" }}
            />
          }
        />
      )}
      <Input
        disableUnderline
        fullWidth
        size={variant === SearchBoxVariant.navbar ? "small" : "medium"}
        variant="filled"
        type="text"
        inputProps={{
          sx: {
            paddingY: "5px",
            typography: "h6",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            fontWeight: 400,
          },
        }}
        sx={{
          "&::placeholder": {
            color: "neutral.600",
          },
          "&:focus": {
            outline: "none",
            border: "none",
          },
          textOverflow: "ellipsis",
          background: "none",
          color: "neutral.900",
        }}
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        spellCheck={spellCheck}
        {...getInputProps()}
      />

      {variant === SearchBoxVariant.navbar && (
        <>
          <ClearInputButton
            value={value}
            buttonRef={clearSearchInputBoxRef}
            onClick={onClear}
            aria-label={clearSearchAriaLabel}
            size="small"
            sx={{
              fontSize: 20,
              padding: 5,
            }}
          />
          <SearchIcon aria-label={searchLabel} />
        </>
      )}
      {variant === SearchBoxVariant.inApp && (
        <>
          <ClearInputButton
            value={value}
            buttonRef={clearSearchInputBoxRef}
            onClick={onClear}
            aria-label={clearSearchAriaLabel}
            size={isMobile ? "small" : "large"}
            sx={(theme: Theme) => ({
              marginRight: 16,
              [theme.breakpoints.down("md")]: {
                marginRight: 4,
                padding: 5,
                fontSize: 20,
              },
            })}
          />
          <Responsive
            Desktop={<SearchButton label={searchLabel} onSubmit={onSubmit} />}
            Mobile={
              <SearchIcon
                aria-label={searchLabel}
                variant="contained"
                color="primary"
              />
            }
          />
        </>
      )}
    </Box>
  );
}

function ClearInputButton({
  value,
  buttonRef,
  sx,
  ...props
}: Omit<IconButtonProps, "icon"> & {
  value: string;
  buttonRef?: React.RefObject<HTMLButtonElement>;
}) {
  if (!value) {
    return null;
  }

  return (
    <IconButton
      icon={faTimes}
      size="medium"
      variant="text"
      color="neutral"
      ref={buttonRef}
      sx={sx}
      {...props}
    />
  );
}

function SearchIcon({ sx, ...props }: Omit<IconButtonProps, "icon">) {
  return (
    <IconButton
      icon={faSearch}
      size="small"
      variant="text"
      color="neutral"
      sx={[
        {
          fontSize: 20,
          padding: 5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      type="submit"
      {...props}
    />
  );
}

function SearchButton({
  label,
  onSubmit,
}: {
  label: string;
  onSubmit: (e: any) => void;
}) {
  return (
    <Button size="large" variant="contained" onClick={onSubmit}>
      {label}
    </Button>
  );
}

const desktopOnly = {
  [THEME.breakpoints.down("md")]: {
    display: "none",
  },
};

const mobileOnly = {
  [THEME.breakpoints.up("md")]: {
    display: "none",
  },
};

function Responsive({
  Mobile,
  Desktop,
}: {
  Mobile?: React.ReactNode;
  Desktop?: React.ReactNode;
}) {
  const mobile = Mobile ? <Box sx={mobileOnly}>{Mobile}</Box> : null;
  const desktop = Desktop ? <Box sx={desktopOnly}>{Desktop}</Box> : null;
  return (
    <>
      {mobile}
      {desktop}
    </>
  );
}

function FilterButton({
  options,
  selectedOption,
  label,
  onChange,
  onOpen,
  onClose,
}: SearchBoxFilterOptions) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const handleSelect = (optionValue: FilterOption | null) => {
    onChange?.(optionValue);
    handleClose();
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="contained"
        sx={(theme: Theme) => ({
          borderRadius: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.4em",
          px: 14,
          height: "100%",
          backgroundColor: isOpen ? "grey.700" : "grey.100",
          color: isOpen ? "white" : "grey.900",
          border: "none",
          boxShadow: "none",
          "&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)":
            {
              backgroundColor: isOpen ? "grey.700" : "grey.300",
              color: isOpen ? "white" : "grey.900",
            },
          [theme.breakpoints.down("md")]: {
            height: "3.4rem",
            fontSize: "1.3rem",
            px: 9,
          },
        })}
      >
        {label}
        <Icon
          icon={faChevronDown}
          sx={{
            color: isOpen ? "white" : "grey.900",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </Button>
      <Menu
        onClose={() => handleClose()}
        open={isOpen}
        anchorEl={anchorEl}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 8,
              marginTop: 8,
            },
          },
        }}
        MenuListProps={{
          sx: {
            py: 0,
            // eslint-disable-next-line i18next/no-literal-string
            border: "2px solid #E5E5E5",
            borderRadius: 8,
          },
        }}
      >
        {options.map(option => (
          <FilterMenuItem
            key={option.value}
            selected={selectedOption?.value === option.value}
            label={option.label}
            onClick={() => handleSelect(option)}
          />
        ))}
      </Menu>
    </>
  );
}

function FilterMenuItem({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <MenuItem
      onClick={onClick}
      sx={(theme: Theme) => ({
        minWidth: 145,
        borderRadius: 0,
        color: "grey.950",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        px: 16,
        py: 12,
        fontWeight: 500,
        [theme.breakpoints.down("md")]: {
          fontSize: "1.3rem",
          minHeight: "inherit",
        },
      })}
    >
      {label}
      <Icon
        icon={faCheck}
        sx={{
          visibility: selected ? "visible" : "hidden",
        }}
      />
    </MenuItem>
  );
}
